import './App.css';
import React, { useState } from 'react';

function App() {
  const [instCode, setInstCode] = useState("");
  const [appKey, setAppKey] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [userId, setUserId] = useState("");
  const [assertion, setAssertion] = useState("");
  const [authorization, setAuthorization] = useState("");
  const [encoding, setEncoding] = useState("base16");

  const [errorMessage, setErrorMessage] = useState("");
  const [assertionCopied, setAssertionCopied] = useState(false);
  const [authorizationCopied, setAuthorizationCopied] = useState(false);


  const base16Decoder = (hexString) => {
    const byteArray = new Uint8Array(hexString.length / 2);
    for (let i = 0; i < byteArray.length; i++) {
      byteArray[i] = parseInt(hexString.substr(i * 2, 2), 16);
    }
    return byteArray;
  };

  const base64UrlEncode = (array) => {
    const base64 = btoa(String.fromCharCode.apply(null, array));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  const createAssertionTokenBase16 = async (privateKeyHex, appKey, appSecret, userId, institution) => {
    const privateKeyBytes = base16Decoder(privateKeyHex);
    const privateKeyObj = await window.crypto.subtle.importKey(
        "pkcs8",
        privateKeyBytes,
        { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
        false,
        ["sign"]
    );

    const payload = {
      iss: appKey,
      instCode: institution,
      exp: Math.floor(Date.now() / 1000) + 123123123,
      userId,
      iat: Math.floor(Date.now() / 1000)
    };

    const header = { alg: "RS256", typ: "JWT" };

    const encodedHeader = base64UrlEncode(new TextEncoder().encode(JSON.stringify(header)));
    const encodedPayload = base64UrlEncode(new TextEncoder().encode(JSON.stringify(payload)));
    const dataToSign = `${encodedHeader}.${encodedPayload}`;

    const signatureBuffer = await window.crypto.subtle.sign(
        "RSASSA-PKCS1-v1_5",
        privateKeyObj,
        new TextEncoder().encode(dataToSign)
    );

    const signatureArray = new Uint8Array(signatureBuffer);
    const encodedSignature = base64UrlEncode(signatureArray);

    return `${dataToSign}.${encodedSignature}`;
  };

  const createAssertionTokenBase64 = async (privateKeyBase64, appKey, appSecret, userId, institution) => {
    const privateKeyBytes = Uint8Array.from(atob(privateKeyBase64), c => c.charCodeAt(0));

    const privateKeyObj = await window.crypto.subtle.importKey(
        "pkcs8",
        privateKeyBytes,
        { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
        false,
        ["sign"]
    );

    const payload = {
      iss: appKey,
      instCode: institution,
      exp: Math.floor(Date.now() / 1000) + 123123123,
      userId,
      iat: Math.floor(Date.now() / 1000)
    };

    const header = { alg: "RS256", typ: "JWT" };

    const encodedHeader = base64UrlEncode(new TextEncoder().encode(JSON.stringify(header)));
    const encodedPayload = base64UrlEncode(new TextEncoder().encode(JSON.stringify(payload)));
    const dataToSign = `${encodedHeader}.${encodedPayload}`;

    const signatureBuffer = await window.crypto.subtle.sign(
        "RSASSA-PKCS1-v1_5",
        privateKeyObj,
        new TextEncoder().encode(dataToSign)
    );

    const signatureArray = new Uint8Array(signatureBuffer);
    const encodedSignature = base64UrlEncode(signatureArray);

    return `${dataToSign}.${encodedSignature}`;
  };

  const generateTokens = async () => {
    if (!privateKey || !appKey || !appSecret || !userId || !instCode) {
      setErrorMessage("⚠️ Please fill in all fields before generating tokens.");
      return;
    }

    setErrorMessage("");
    setAssertion("");
    setAuthorization("");

    try {
      let assertionJWT;

      if (encoding === "base16") {
        assertionJWT = await createAssertionTokenBase16(
            privateKey,
            appKey,
            appSecret,
            userId,
            instCode
        );
      } else {
        assertionJWT = await createAssertionTokenBase64(
            privateKey,
            appKey,
            appSecret,
            userId,
            instCode
        );
      }

      const authorizationStr = btoa(`${appKey}:${appSecret}`);

      setAssertion(assertionJWT);
      setAuthorization(`Basic ${authorizationStr}`);

    } catch (error) {
      console.error("Assertion generation failed:", error);

      setErrorMessage(
          "Assertion generation failed. Please check your private key format and encoding type."
      );
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);

      if (type === "assertion") {
        setAssertionCopied(true);
        setTimeout(() => setAssertionCopied(false), 2000);
      } else {
        setAuthorizationCopied(true);
        setTimeout(() => setAuthorizationCopied(false), 2000);
      }
    } catch (err) {
      console.error("Clipboard copy failed:", err);

      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      if (type === "assertion") {
        setAssertionCopied(true);
        setTimeout(() => setAssertionCopied(false), 2000);
      } else {
        setAuthorizationCopied(true);
        setTimeout(() => setAuthorizationCopied(false), 2000);
      }
    }
  };


  return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.title}>Generate Assertion</h2>

          {errorMessage && <div style={styles.error}>{errorMessage}</div>}

          <div style={styles.row}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Institution Code</label>
              <input style={styles.input} value={instCode} onChange={(e) => setInstCode(e.target.value)} />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>App Key</label>
              <input style={styles.input} value={appKey} onChange={(e) => setAppKey(e.target.value)} />
            </div>
          </div>

          <div style={styles.row}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>App Secret</label>
              <input style={styles.input} value={appSecret} onChange={(e) => setAppSecret(e.target.value)} />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>User ID</label>
              <input style={styles.input} value={userId} onChange={(e) => setUserId(e.target.value)} />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Private Key</label>
            <textarea style={styles.textarea} rows={6} value={privateKey} onChange={(e) => setPrivateKey(e.target.value)} />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Encoding</label>
            <select style={styles.input} value={encoding} onChange={(e) => setEncoding(e.target.value)}>
              <option value="base16">Base16</option>
              <option value="base64">Base64</option>
            </select>
          </div>

          <button style={styles.button} onClick={generateTokens}>Generate</button>

          {assertion && (
              <div style={styles.output}>
                <div style={styles.headerRow}>
                  <h3 style={styles.outputTitle}>Assertion Token</h3>
                  <span
                      style={styles.copyIconHeader}
                      onClick={() => copyToClipboard(assertion, "assertion")}
                  >
      📋
    </span>
                </div>

                <textarea style={styles.largeTextarea} value={assertion} readOnly />

                {assertionCopied && (
                    <div style={styles.successMsg}>✔ Assertion copied</div>
                )}
              </div>

          )}

          {authorization && (
              <div style={styles.output}>
                <div style={styles.headerRow}>
                  <h3 style={styles.outputTitle}>Authorization</h3>
                  <span
                      style={styles.copyIconHeader}
                      onClick={() => copyToClipboard(authorization, "authorization")}
                  >
      📋
    </span>
                </div>

                <textarea style={styles.smallTextarea} value={authorization} readOnly />

                {authorizationCopied && (
                    <div style={styles.successMsg}>✔ Authorization copied</div>
                )}
              </div>

          )}


        </div>
      </div>
  );
}

// Styles
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    backgroundColor: '#f0f2f5',
    padding: '10px'
  },
  card: {
    backgroundColor: '#fff',
    padding: '35px',
    borderRadius: '15px',
    boxShadow: '0 6px 25px rgba(0,0,0,0.15)',
    width: '950px',
    maxWidth: '95%'
  },
  title: {
    textAlign: 'center',
    marginBottom: '25px',
    fontSize: '24px',
    fontWeight: '600',
    color: '#2c3e50'
  },
  error: {
    color: "#e74c3c",
    marginBottom: "15px",
    fontWeight: "500",
    fontSize: "15px",
  },
  copiedMsg: {
    color: "#27ae60",
    fontSize: "14px",
    marginTop: "5px",
    fontWeight: "500",
  },
  inputGroup: {
    marginBottom: '18px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1
  },
  label: {
    marginBottom: '6px',
    fontWeight: '500',
    color: '#34495e'
  },
  input: {
    padding: '10px 12px',
    fontSize: '15px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    outline: 'none',
    transition: 'border-color 0.3s'
  },
  textarea: {
    padding: '10px 12px',
    fontSize: '15px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    outline: 'none',
    resize: 'vertical',
    transition: 'border-color 0.3s',
    fontFamily: "'Courier New', monospace"
  },
  largeTextarea: {
    padding: '12px',
    fontSize: '14px',
    borderRadius: '10px',
    border: '1px solid #ccc',
    outline: 'none',
    width: '100%',
    minHeight: '80px',
    resize: 'vertical',
    backgroundColor: '#f9f9f9',
    fontFamily: "'Courier New', monospace"
  },
  smallTextarea: {
    padding: '12px',
    fontSize: '14px',
    borderRadius: '10px',
    border: '1px solid #ccc',
    outline: 'none',
    width: '100%',
    minHeight: '20px',
    resize: 'vertical',
    backgroundColor: '#f9f9f9',
    fontFamily: "'Courier New', monospace"
  },
  button: {
    padding: '12px 25px',
    borderRadius: '10px',
    backgroundColor: 'darkblue',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    marginTop: '10px',
  },
  output: {
    marginTop: '25px'
  },
  outputTitle: {
    fontSize: '18px',
    marginBottom: '6px',
    color: '#2c3e50'
  },
  copyButton: {
    marginTop: '6px',
    padding: '6px 12px',
    borderRadius: '8px',
    backgroundColor: '#3498db',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '500'
  },
  row: {
    display: 'flex',
    gap: '20px',
    marginBottom: '18px'
  },
  textareaWrapper: {
    position: "relative",
  },

  copyIcon: {
    position: "absolute",
    right: "10px",
    top: "10px",
    cursor: "pointer",
    fontSize: "20px",
    opacity: 0.6,
    transition: "0.2s",
  },

  successMsg: {
    color: "green",
    marginTop: "6px",
    fontSize: "14px",
    fontWeight: "500"
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "6px"
  },

  copyIconHeader: {
    cursor: "pointer",
    fontSize: "20px",
    opacity: 0.7,
    transition: "0.2s",
  },

  copyIconHeaderHover: {
    opacity: 1
  }


};

export default App;
